import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { cn } from '@beatq/ui';
import { getEmergencyStatus, loadDashboardSummary } from '../features/auth/adminApi.js';
import { AdminIcon } from './adminIcons.js';

export function AdminNotificationsBell() {
  const emergencyQuery = useQuery({
    queryKey: ['admin', 'shell', 'emergency-status'],
    queryFn: getEmergencyStatus,
    refetchInterval: 30_000,
  });
  const summaryQuery = useQuery({
    queryKey: ['admin', 'dashboard-summary'],
    queryFn: loadDashboardSummary,
    staleTime: 15_000,
    // Poll fallback; shell useAdminPlatformRealtime invalidates this key on socket events.
    refetchInterval: 30_000,
  });

  const emergencyActive = (() => {
    const env = emergencyQuery.data;
    if (env == null || typeof env !== 'object') return false;
    const data = (env as { data?: { active?: boolean; status?: string } }).data ?? env;
    const d = data as { active?: boolean; status?: string };
    return d.active === true || d.status === 'active' || d.status === 'PAUSED';
  })();

  const unread = summaryQuery.data?.unreadAlertCount ?? 0;
  const healthy = summaryQuery.data?.notificationsHealthy !== false && !emergencyActive;
  const toneLabel = emergencyActive
    ? 'Emergency active'
    : unread > 0
      ? `${unread} unread notification${unread === 1 ? '' : 's'}`
      : healthy
        ? 'Notifications healthy'
        : 'Notifications degraded';

  return (
    <Link
      to="/notification-centre"
      className={cn(
        'relative inline-flex min-h-11 min-w-11 items-center justify-center rounded-control border border-line bg-raised text-muted no-underline',
        'hov:bg-sunken hov:text-default',
      )}
      aria-label={`Notifications, ${toneLabel}`}
      title={toneLabel}
    >
      <AdminIcon screenId="SCR-ADM-046" size={18} />
      <span
        className={cn(
          'absolute right-1.5 top-1.5 min-h-2 min-w-2 rounded-pill border border-current',
          emergencyActive
            ? 'bg-blocked'
            : unread > 0
              ? 'bg-late'
              : healthy
                ? 'bg-ready'
                : 'bg-late',
        )}
        aria-hidden
      />
      {unread > 0 ? (
        <span className="sr-only">{unread} unread</span>
      ) : (
        <span className="sr-only">{healthy ? 'Live' : 'Degraded'}</span>
      )}
    </Link>
  );
}
